import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Calendar, CheckCircle2, Loader2, Link, Unlink, X } from 'lucide-react';

/**
 * CalendarConnect — shows connection status for Google / Outlook and lets
 * the employer connect/disconnect each provider.
 *
 * Usage: <CalendarConnect onClose={() => setShowCal(false)} />
 */
export function CalendarConnect({ onClose }) {
  const { api } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/calendar/status')
      .then(r => setStatus(r.data))
      .catch(() => toast.error('Could not load calendar status'))
      .finally(() => setLoading(false));
  }, []);

  const connect = async (provider) => {
    try {
      const res = await api.get(`/calendar/${provider}/connect`);
      window.location.href = res.data.url;
    } catch (e) {
      const msg = e.response?.data?.detail || 'Not configured';
      toast.error(`${provider === 'google' ? 'Google' : 'Outlook'} Calendar: ${msg}`);
    }
  };

  const disconnect = async (provider) => {
    await api.delete(`/calendar/disconnect/${provider}`);
    setStatus(prev => ({ ...prev, [provider]: { ...prev[provider], connected: false } }));
    toast.success(`${provider} calendar disconnected`);
  };

  const PROVIDERS = [
    {
      key: 'google',
      name: 'Google Calendar',
      icon: '🗓️',
      color: 'border-blue-200 bg-blue-50',
      btnColor: 'bg-[#4285F4] hover:bg-[#3367D6] text-white',
    },
    {
      key: 'microsoft',
      name: 'Outlook / Microsoft 365',
      icon: '📅',
      color: 'border-blue-300 bg-blue-50',
      btnColor: 'bg-[#0078D4] hover:bg-[#106EBE] text-white',
    },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#003366]" />
            <h3 className="font-bold text-lg">Calendar Integration</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mb-5">
          Connect your calendar to create interview events and send invites automatically when you schedule an interview.
        </p>

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#003366]" /></div>
        ) : (
          <div className="space-y-3">
            {PROVIDERS.map(p => {
              const s = status?.[p.key] || {};
              return (
                <div key={p.key} className={`border rounded-xl p-4 ${p.color}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{p.icon}</span>
                      <div>
                        <p className="font-semibold text-sm">{p.name}</p>
                        <p className={`text-xs mt-0.5 ${s.connected ? 'text-green-600' : 'text-muted-foreground'}`}>
                          {s.connected ? '✓ Connected' : s.configured ? 'Not connected' : 'Not configured (add API keys)'}
                        </p>
                      </div>
                    </div>
                    {s.connected ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => disconnect(p.key)}
                      >
                        <Unlink className="w-3.5 h-3.5 mr-1" />Disconnect
                      </Button>
                    ) : s.configured ? (
                      <Button size="sm" className={p.btnColor} onClick={() => connect(p.key)}>
                        <Link className="w-3.5 h-3.5 mr-1" />Connect
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground bg-white border rounded-lg px-2 py-1">Setup needed</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-4 bg-gray-50 rounded-lg p-3">
          After connecting, when you schedule an interview in the Pipeline or Applicants page, a calendar event with a Google Meet / Teams link is automatically created and invites are sent to both you and the candidate.
        </p>
      </div>
    </div>
  );
}
